<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { postReq } from '../../../../services/http';
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Input, toast } from '@Shelly/shelly-design-system';
	import SyncName from '../../ApplicationSettings/SyncName.svelte';

	export let device: Device;

	const id = real_id(device.id);

	$: storeDevice = $devices[device.id] ?? device;
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? readDeviceSettings(id);
	$: synced = !!storeDevice.name_sync;

	let name = '';
	let cache = '';
	let loading = false;
	let initialized = false;

	$: if (settings || storeDevice) {
		const nextName =
			typeof settings?.sys?.device?.name === 'string' && settings.sys.device.name.trim() !== ''
				? settings.sys.device.name
				: (storeDevice.name ?? '');

		if (!initialized) {
			name = nextName;
			cache = name;
			initialized = true;
		} else if (!loading && name === cache && nextName !== cache) {
			// Follow device/settings updates when the field is not mid-edit
			name = nextName;
			cache = nextName;
		}
	}

	$: error = name === '' && synced;

	const saveName = async () => {
		name = name.trim();
		if (cache === name || name === '') return;

		loading = true;
		const params = {
			id: device.channel,
			config: { device: { name } },
		};

		if (synced) {
			const res: SettingRequest = await gen2Request(device, 'Sys.SetConfig', params);
			if (!res.isok) {
				toast.open({ type: 'danger', title: $format('error_global') });
				loading = false;
				return;
			}
		}

		const updated: Device = { ...storeDevice, name };
		devices.update(updated);
		const int_res = await postReq('interface/device/save', {
			id,
			data: JSON.stringify(updated),
		});

		if (int_res.isok) {
			cache = name;
			if (settings?.sys?.device) {
				settings.sys.device.name = name;
				devices_settings.update_devices_settings(id, settings);
			}
			toast.open({
				type: 'success',
				title:
					$format('device_name_scc_toast', { values: { value: name } }) ||
					`Device name changed to ${name}`,
			});
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		loading = false;
	};
</script>

<Input
	size="m"
	class="mb-s"
	label={$format('sub_settings_title_device_name')}
	bind:value={name}
	on:enter={saveName}
	id="input-r88ut8o"
	aria-labelledby="input-r88ut8o"
/>
<SyncName
	nameType="device"
	device={storeDevice}
	defaultTarget="sys"
	info={$format('device_rename_description_g2_sys', {
		default:
			'Keep the device name in sync with the name given in the application if "Sync name" is enabled. When sync is enabled, changes on the device system name also update the app.',
	})}
/>
<Button
	class="mt-s"
	{loading}
	size="m"
	width="full"
	on:click={saveName}
	disabled={name.length <= 0 || error}>{$format('button_save')}</Button
>
